import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { FOCUS_RING } from './tone';

// Phase 36 §21a — buttons and inputs.
//
// One filled action per surface. Everything else is outlined, and hover moves
// the BORDER rather than the fill: a control that changes colour under the
// cursor competes with the status colours, which are the only chroma on the
// screen that carries meaning.

const MONO = 'co-mono';

/** 34px, 8px radius — the height every control in the product agrees on. */
const BASE =
  'inline-flex h-[34px] flex-none items-center justify-center gap-[7px] whitespace-nowrap ' +
  'rounded-lg px-[14px] text-[12.5px] transition-colors ' +
  FOCUS_RING;

export type ButtonVariant = 'primary' | 'secondary' | 'destructive';

const VARIANT: Record<ButtonVariant, string> = {
  // `--co-brass-ink`, not `--co-brass`. White on #A67C3D is 3.53:1, which fails
  // every text threshold there is; the darker tone is the fill and the mid tone
  // stays a border colour.
  primary:
    'border border-transparent bg-[var(--co-brass-ink)] font-bold text-[var(--co-brass-on)] ' +
    'hover:bg-[var(--co-brass-hover)]',
  secondary:
    'border border-[var(--co-line-strong)] bg-[var(--co-raised)] text-[var(--co-ink-2)] ' +
    'hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]',
  // Outlined, never filled. A filled red button is the one people press by
  // accident, and it is the one action where that matters most.
  destructive:
    'border border-[var(--co-burgundy-line)] bg-[var(--co-burgundy-wash)] font-semibold ' +
    'text-[var(--co-burgundy)] hover:border-[var(--co-burgundy)]',
};

const DISABLED =
  'border border-[var(--co-line)] bg-[var(--co-raised)] text-[var(--co-ink-4)] ' +
  'cursor-not-allowed';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  children: ReactNode;
  /** Rendered in mono beside the label. Omit rather than pass a zero. */
  count?: number;
  /**
   * Why this button cannot be pressed, shown beside it.
   *
   * Passing a reason disables the button. A dead control with no explanation is
   * worse than an enabled one that fails — the failure at least says something.
   */
  disabledReason?: string;
}

export function Button({
  variant = 'secondary',
  children,
  count,
  disabledReason,
  disabled,
  className = '',
  ...rest
}: ButtonProps) {
  const off = disabled || Boolean(disabledReason);
  const button = (
    <button
      type="button"
      disabled={off}
      className={`${BASE} ${off ? DISABLED : VARIANT[variant]} ${className}`}
      {...rest}
    >
      {children}
      {count !== undefined && (
        <span
          className={`${MONO} text-[10.5px] font-semibold tabular-nums`}
          style={{
            color: variant === 'primary' ? 'var(--co-brass-pale)' : 'var(--co-brass-ink)',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );

  if (!disabledReason) return button;
  return (
    <span className="inline-flex items-center gap-[9px]">
      {button}
      <span
        className={`${MONO} text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-terracotta-ink)]`}
      >
        {disabledReason}
      </span>
    </span>
  );
}

/** Square, 34px, and always labelled — an unlabelled glyph is a guess. */
export function IconButton({
  label,
  children,
  className = '',
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      className={
        'inline-flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg ' +
        'border border-[var(--co-line-strong)] bg-[var(--co-raised)] text-[var(--co-ink-2)] ' +
        'transition-colors hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
        'disabled:cursor-not-allowed disabled:opacity-50 ' +
        `${FOCUS_RING} ${className}`
      }
      {...rest}
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------- inputs

const FIELD =
  'h-[34px] w-full rounded-lg border bg-[var(--co-raised)] px-3 text-[13px] ' +
  'text-[var(--co-ink)] placeholder-[var(--co-ink-4)] outline-none transition-colors';

/**
 * Focus moves the border to brass and does nothing else.
 *
 * No ring, no glow: a coloured halo on a focused field is a second chroma
 * competing with the status colours, and it reads as an error to anyone who
 * has filled in a form before.
 */
export function Input({
  error,
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { error?: string }) {
  return (
    <div className="min-w-0">
      <input
        aria-invalid={error ? true : undefined}
        className={
          `${FIELD} ` +
          (error
            ? 'border-[var(--co-burgundy)] '
            : 'border-[var(--co-line-strong)] focus:border-[var(--co-brass)] ') +
          'disabled:bg-[var(--co-sunken)] disabled:text-[var(--co-ink-4)] ' +
          className
        }
        {...rest}
      />
      {/* The message says what to do. "Invalid input" states the conclusion the
          reader already drew from the red border. */}
      {error && (
        <p className="mt-[5px] text-[11.5px] leading-[1.45] text-[var(--co-burgundy)]">
          {error}
        </p>
      )}
    </div>
  );
}

export function SearchInput({
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={`relative min-w-0 ${className}`}>
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-[13px] w-[13px] -translate-y-1/2"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--co-ink-4)"
        strokeWidth="1.9"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      {/* The placeholder names every field it matches. A bare "Search…" makes
          the reader guess whether it reads bodies, slugs, or only titles. */}
      <input
        type="search"
        className={`${FIELD} border-[var(--co-line-strong)] pl-9 focus:border-[var(--co-brass)]`}
        {...rest}
      />
    </div>
  );
}

export function Textarea({
  className = '',
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={
        'min-h-[56px] w-full rounded-lg border border-[var(--co-line-strong)] ' +
        'bg-[var(--co-raised)] px-3 py-[10px] text-[12.5px] leading-[1.5] ' +
        'text-[var(--co-ink-2)] outline-none transition-colors ' +
        'placeholder-[var(--co-ink-4)] focus:border-[var(--co-brass)] ' +
        className
      }
      {...rest}
    />
  );
}

/**
 * A select-shaped BUTTON, never a native `<select>`.
 *
 * The native dropdown is painted by the browser in the browser's own blue — the
 * one chroma on a screen whose entire colour vocabulary is four status hues.
 * It is also unstyleable on every platform that matters.
 */
export function SelectButton({
  label,
  value,
  className = '',
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> & {
  label?: string;
  value: ReactNode;
}) {
  return (
    <button
      type="button"
      className={
        'inline-flex h-[34px] flex-none items-center gap-2 rounded-lg border ' +
        'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[13px] ' +
        'text-[var(--co-ink)] transition-colors hover:border-[var(--co-brass)] ' +
        `${FOCUS_RING} ${className}`
      }
      {...rest}
    >
      {label && (
        <span
          className={`${MONO} text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-4)]`}
        >
          {label}
        </span>
      )}
      {value}
      <span className="text-[var(--co-ink-3)]" aria-hidden="true">
        ▾
      </span>
    </button>
  );
}
